<template>
  <div class="camp-task-detail">
    <div class="detail-card">
      <div class="field">
        <div class="field-label">任务类型</div>
        <div class="field-value">{{ typeLabel }}</div>
      </div>
      <!-- 关联课程 -->
      <div class="field" v-if="type === 'course'">
        <div class="field-label">关联课程</div>
        <div class="course-row">
          <img class="course-cover" :src="detail.coverUrl" :title="detail.name || ''" alt="">
          <div class="course-main">
            <div class="course-title">{{ detail.name || '' }}</div>
            <div class="course-hospital">{{ detail.courseHospitalName || '' }}</div>
          </div>
        </div>
      </div>
      <!-- 考试名称、题目 -->
      <template v-if="type === 'exam'">
        <div class="field">
          <div class="field-label">考试名称</div>
          <div class="field-value">{{ detail.name || '' }}</div>
        </div>
        <div class="field is-full">
          <div class="field-label">考试题目（{{ questions.length }}道）</div>
          <div class="question" v-for="item in questions" :key="item.questionId + '-' + item.sortNo">
            <div class="question-title">{{ item.title }}</div>
            <div class="question-meta">
              <span class="question-tag">{{ item.category }}</span>
              <span>{{ item.code }}</span>
              <span class="question-score">本题 {{ item.score }}分</span>
            </div>
          </div>
        </div>
      </template>
      <!-- 消息内容 -->
      <div class="field" v-if="type === 'message'">
        <div class="field-label">消息内容</div>
        <div class="field-value">{{ detail.messageContent || '' }}</div>
      </div>
      <div class="field is-full">
        <div class="field-label">推送时间</div>
        <div class="field-value">{{ formatDateTime(detail.createdAt) }}</div>
      </div>
      <!-- 被推送学员 -->
      <div class="field">
        <div class="field-label">被推送学员（{{ students.length }}人）</div>
        <div class="student-row" v-for="item in students" :key="item.campMemberId">
          <img class="student-avatar" src="@/assets/images/researchCamp/avatar.png" alt="">
          <div class="student-main">
            <div class="student-name">{{ item.name }}</div>
            <div class="student-hospital">{{ item.hospitalText }}</div>
          </div>
          <button class="remove-btn" type="button" :disabled="memberBusy" @click="removeStudent(item)"></button>
        </div>
        <div class="detail-add" @click="pickerVisible = true">追加学员</div>
      </div>
    </div>
    <!-- 选择学员 -->
    <camp-student-picker
      v-model="pickerVisible"
      :students="appendStudents"
      :selected-ids="[]"
      :loading="candidateLoading"
      @confirm="confirmStudents"
    ></camp-student-picker>
  </div>
</template>

<script>
import withRouteTracking from '@/utils/withRouteTracking'
import CampStudentPicker from './components/CampStudentPicker.vue'
import {
  appendResearchCampDispatchMembers,
  getResearchCampDispatchStudentCandidates,
  getResearchCampDispatchDetail,
  removeResearchCampDispatchMember
} from '@/api/researchCamp'

const CampCustomTaskDetail = {
  name: 'CampCustomTaskDetail',
  components: {
    CampStudentPicker
  },
  data () {
    return {
      dispatchId: '',
      candidateLoading: false,
      memberBusy: false,
      detail: {},
      pickerVisible: false,
      allStudents: []
    }
  },
  computed: {
    // 任务类型：课程、考试或消息
    type () {
      return this.$route.query.type || 'course'
    },
    // 任务类型中文名
    typeLabel () {
      return { course: '课程', exam: '考试', message: '消息' }[this.type] || '课程'
    },
    // 考试题目，详情未返回时按空列表处理
    questions () {
      const list = this.detail && this.detail.questions
      return Array.isArray(list) ? list : []
    },
    // 已分配学员，副标题为医院和科室
    students () {
      return ((this.detail && this.detail.members) || []).map(item => {
        return Object.assign({}, item, {
          hospitalText: [item.hospitalName, item.departmentL1, item.positionTitle].filter(Boolean).join(' ')
        })
      })
    },
    // 已分配学员 id
    selectedIds () {
      return this.students.map(item => String(item.campMemberId))
    },
    // 追加弹窗只列出还没推送的学员
    appendStudents () {
      const selected = this.selectedIds
      return this.allStudents.filter(item => selected.indexOf(String(item.id)) === -1)
    }
  },
  created () {
    this.dispatchId = this.$route.query.dispatchId || this.$route.query.taskId || ''
    this.loadDetail()
    this.loadCandidates()
  },
  methods: {
    // 加载定制任务详情
    async loadDetail () {
      if (!this.dispatchId) return
      try {
        const response = await getResearchCampDispatchDetail(this.dispatchId)
        this.detail = response && response.data ? response.data : {}
        if (!this.$route.query.researchCampId && !this.$route.query.campConfigId) this.loadCandidates()
      } catch (error) {
        this.$toast((error && error.msg) || '定制任务加载失败')
      }
    },
    // 加载可追加的学员。详情尚未返回时用路由里的研修营编号
    async loadCandidates () {
      const campConfigId = this.$route.query.researchCampId || this.$route.query.campConfigId || this.detail.campConfigId
      if (!campConfigId) return
      this.candidateLoading = true
      try {
        const response = await getResearchCampDispatchStudentCandidates(campConfigId, {})
        const rows = response && Array.isArray(response.data) ? response.data : []
        this.allStudents = rows.map(item => ({
          id: String(item.campMemberId),
          campMemberId: String(item.campMemberId),
          name: item.name || '',
          hospital: [item.hospitalName, item.departmentL1, item.positionTitle]
            .filter(Boolean).join(' ')
        }))
      } catch (error) {
        this.allStudents = []
        this.$toast((error && error.msg) || '学员列表加载失败')
      } finally {
        this.candidateLoading = false
      }
    },
    // 从任务中移除学员
    async removeStudent (item) {
      this.memberBusy = true
      try {
        await removeResearchCampDispatchMember(this.dispatchId, item.campMemberId)
        this.$toast('已移除学员')
        await this.loadDetail()
      } catch (error) {
        this.$toast((error && error.msg) || '移除学员失败')
      } finally {
        this.memberBusy = false
      }
    },
    // 只追加新勾选的学员
    async confirmStudents (ids) {
      const currentIds = this.selectedIds
      const newIds = ids.filter(id => currentIds.indexOf(String(id)) === -1)
      if (!newIds.length) return
      this.memberBusy = true
      try {
        await appendResearchCampDispatchMembers(this.dispatchId, { campMemberIds: newIds })
        this.$toast('已追加学员')
        await this.loadDetail()
      } catch (error) {
        this.$toast((error && error.msg) || '追加学员失败')
      } finally {
        this.memberBusy = false
      }
    },
    // 创建时间格式化
    formatDateTime (value) {
      if (!value) return ''
      const date = new Date(Number(value))
      if (Number.isNaN(date.getTime())) return String(value)
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
  }
}

export default withRouteTracking(CampCustomTaskDetail)
</script>

<style lang="less" scoped>
@import './camp.less';

.camp-task-detail {
  .camp-page();
  padding: 12px 0 24px;

  .detail-card {
    .camp-white-card();
    padding: 16px 14px 24px;
  }

  .field + .field {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #e8e8e8;
  }

  .is-full {
    margin-left: -14px;
    margin-right: -14px;
    padding-left: 14px;
    padding-right: 14px;
  }

  .field-label {
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: #81766f;
  }

  .field-value {
    margin-top: 4px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #000;
  }

  .course-row {
    display: flex;
    align-items: flex-start;
    margin-top: 8px;
  }

  .course-cover {
    width: 100px;
    height: 58px;
    margin-right: 10px;
    border-radius: 5px;
    object-fit: cover;
    flex-shrink: 0;
  }

  .course-main {
    flex: 1;
    min-width: 0;
  }

  .course-title {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #000;
  }

  .course-hospital {
    font-size: 11px;
    font-weight: 400;
    line-height: 20px;
    color: #a39992;
  }

  .question {
    padding-top: 12px;
    padding-bottom: 14px;

    & + .question {
      border-top: 1px solid #e8e8e8;
    }
  }

  .question-title {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #000;
  }

  .question-meta {
    display: flex;
    align-items: center;
    margin-top: 6px;
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: #81766f;
  }

  .question-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 59px;
    height: 18px;
    margin-right: 6px;
    padding: 0 6px;
    border-radius: 10px;
    background: rgba(235, 23, 0, 0.08);
    color: #eb1700;
    font-size: 11px;
    line-height: 18px;
  }

  .question-score {
    margin-left: auto;
    flex-shrink: 0;
    padding-left: 12px;
    color: #81766f;
  }

  .student-row {
    display: flex;
    align-items: center;
    margin-top: 16px;

    & + .student-row {
      margin-top: 22px;
    }
  }

  .student-avatar {
    width: 42px;
    height: 42px;
    margin-right: 9px;
    border-radius: 50%;
    object-fit: cover;
    overflow: hidden;
    flex-shrink: 0;
  }

  .student-main {
    flex: 1;
    min-width: 0;
  }

  .student-name {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #000;
  }

  .student-hospital {
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: #81766f;
  }

  .remove-btn {
    position: relative;
    width: 14px;
    height: 14px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(163, 153, 146, 0.5);
    flex-shrink: 0;

    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 3px;
      top: 6px;
      width: 8px;
      height: 1px;
      background: #fff;
    }

    &::before {
      transform: rotate(45deg);
    }

    &::after {
      transform: rotate(-45deg);
    }
  }

  .detail-add {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    margin-top: 24px;
    border: 1px solid @camp-red;
    border-radius: 22px;
    color: @camp-red;
    background: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/researchCamp/customTaskDetail.vue